import {groupBy, includes, hidashIncludes, sortBy} from '@shared/helpers/collection'
import {pipe} from '@shared/helpers/function'
import {eq, isEmpty, isArray, isNumber} from '@shared/helpers/lang'

import {
    Card,
    CardCodeType,
    CardOwnerGroup,
    CardType,
    InterestBurdenType,
    InterestBurdenTypeType,
    InterestFreeInstallmentType,
    InterestFreeInstallmentTypeType,
    InterestFreeStatusType,
    LongFreeInstallmentStatus,
    LongFreeInstallmentStatusType,
    PayMethod,
    PaymentsCardCode,
    PaymentsCardCodeType,
} from '@web/core/constants'

import {CardView} from '../interfaces'
import {InterestFreeInstallment} from '../interfaces/card'

import {InterestInstallment} from './utils'

/** 카드 할부 정보 */
export type CardInstallmentInfo = {
    /**
     * 할부 기간 명
     * 1: 일시불
     * N: N개월 할부
     **/
    title: string
    /** 할부 개월 수 */
    month: number
    /** 무이자 할부 정보 */
    freeType: InterestFreeInstallmentTypeType
    /** 무이자 타입 (PG, MP, MP_SELLER) */
    interestBurdenType?: InterestBurdenTypeType
    /** 부분 무이자 할부 목록 */
    partialInterestMonths: Maybe<number[]>
    /**
     * 특정 카드 프로모션 > 장기무이자
     * 특정카드 장기무이자의 경우 applicableCardProductCode 값이 존재 > LONG_FREE
     * applicableCardProductCode 값이 없는 경우 > NONE
     * */
    longFreeInstallmentType?: LongFreeInstallmentStatusType
}

type CardCompanyCode = PaymentsCardCodeType | string

/** 카드 결제수단 */
type CardPayMethod = keyof Pick<typeof PayMethod, 'CARD' | 'CARD_EASY_PAY'>

/** 무이자 할부 여부 */
const isFreeInstallment = eq(InterestFreeInstallmentType.ALL)

/** 부분 무이자 할부 여부 */
const isPartFreeInstallment = eq(InterestFreeInstallmentType.PART)

const isCardPayMethod = eq(PayMethod.CARD)

/** 일시불 정보 생성 */
const createNonInstallment = (): CardInstallmentInfo => {
    return {
        title: '일시불',
        month: 1,
        freeType: 'NONE',
        partialInterestMonths: null,
    }
}

/** 무이자 여부 */
const getIsFreeInstallment = ({paymentAmount, minimum}: {paymentAmount: number; minimum: number}) =>
    paymentAmount >= minimum

/** 부분 무이자 여부 */
const getIsPartFreeInstallment = ({
    paymentAmount,
    minimum,
    type,
}: {
    paymentAmount: number
    minimum: number
    type: InterestFreeInstallmentTypeType
}) => getIsFreeInstallment({paymentAmount, minimum}) && type === InterestFreeInstallmentType.PART

/** 무이자/부분 무이자 여부 */
function getInterestFreeType({
    month,
    paymentAmount,
    minimum,
    freeType,
    ownerType,
}: {
    month: number
    paymentAmount: number
    minimum: number
    freeType: InterestFreeInstallmentTypeType
    ownerType: Maybe<CardCodeType>
}): InterestFreeInstallmentTypeType {
    if (
        month === 1 ||
        freeType === InterestFreeInstallmentType.NONE ||
        // 법인 (개인사업자 법인 포함) 카드는 할부/무이자혜택 적용 불가
        ownerType === CardOwnerGroup.CORPORATION
    ) {
        return InterestFreeInstallmentType.NONE
    }
    if (getIsPartFreeInstallment({paymentAmount, minimum, type: freeType})) {
        return InterestFreeInstallmentType.PART
    }
    if (getIsFreeInstallment({paymentAmount, minimum})) {
        return InterestFreeInstallmentType.ALL
    }
    return InterestFreeInstallmentType.NONE
}

// 할부 개월수 기준으로 오름차순으로 정렬합니다.
const sortInstallmentMonth = (a: InterestFreeInstallment, b: InterestFreeInstallment) =>
    a.installmentMonth < b.installmentMonth ? -1 : 1

/**
 * 카드 할부 선택 옵션 생성
 * https://oss.fin.navercorp.com/payment-fe/order-web/pull/3952
 * */
export function buildCardInstallmentOptions({
    paymentAmount,
    cardPayMethod,
    companyCode,
    cardType = Card.CREDIT,
    ownerType = CardOwnerGroup.PRIVATE,
    nonInstallmentCardCorps = [],
    installments,
}: {
    /** 결제 금액 */
    paymentAmount: number
    /** 결제수단 */
    cardPayMethod: CardPayMethod
    /** 카드사 코드 */
    companyCode: CardCompanyCode
    /** 카드 타입 */
    cardType?: CardType
    /** 카드 타입 (개인 / 법인) */
    ownerType?: Maybe<CardCodeType>
    /** 카드 상품 코드 */
    cardProductCode?: Maybe<string>
    /** 일시불만 가능한 카드사 목록 */
    nonInstallmentCardCorps?: CardCompanyCode[]
    /** 무이자 할부 정보 */
    installments: InterestFreeInstallment[]
}): CardInstallmentInfo[] {
    if (isEmpty(installments) || !companyCode) {
        return []
    }

    if (
        // 신용카드 아닌 카드는 일시불만 가능
        cardType !== Card.CREDIT ||
        hidashIncludes(nonInstallmentCardCorps, companyCode) ||
        // 카드 코드가 없거나, 할부가 없다면
        !companyCode ||
        isEmpty(installments) ||
        // 모든 할부의 최소금액에 상품가격이 충족되지 않는 경우
        !installments.some((installment) => installment.minAmount <= paymentAmount)
    ) {
        return [createNonInstallment()]
    }

    // 법인카드는 장기무이자를 제한합니다 (할부 제한이 걸려있는 경우)
    // 오름차순 정렬
    return (
        (
            ownerType === CardOwnerGroup.CORPORATION
                ? installments.filter(({installmentMonth}) => installmentMonth < 13)
                : installments
        )
            .sort(sortInstallmentMonth)
            // 현재 결제 금액이 할부 최소 적용 금액보다 적으면 제외
            .filter((installment) => isNumber(installment.minAmount) && installment.minAmount <= paymentAmount)
            .map((installment) => {
                if (installment.installmentMonth === 1) {
                    return createNonInstallment()
                }

                const interestFreeType = getInterestFreeType({
                    paymentAmount,
                    minimum: installment.interestFreeMinimumAmount,
                    ownerType,
                    month: installment.installmentMonth,
                    freeType: installment.interestFreeType,
                })

                const hasPartialInterestMonths =
                    isPartFreeInstallment(interestFreeType) && isArray(installment.customerBurdenMonths)

                return {
                    /**
                     * 부분/무이자 정보가 포함된 Title
                     **/
                    title: (function () {
                        /**
                         * 일반결제 > 신용카드의 경우 특정 카드를 선택하여 결제하는게 아니므로 특정 카드에 대한 무이자 행사가 있더라도 무이자 표기 X
                         */
                        if (isCardPayMethod(cardPayMethod) && !!installment.applicableCardProductCode) {
                            return `${installment.installmentMonth}개월`
                        }
                        return `${installment.installmentMonth}개월${
                            isFreeInstallment(interestFreeType) || isPartFreeInstallment(interestFreeType)
                                ? ` ${isPartFreeInstallment(interestFreeType) ? '부분 ' : ''}무이자`
                                : ''
                        }`
                    })(),
                    month: installment.installmentMonth,
                    freeType: interestFreeType,
                    interestBurdenType: installment.interestBurdenType,
                    partialInterestMonths: hasPartialInterestMonths ? installment.customerBurdenMonths : null,
                    /**
                     * applicableCardProductCode 존재 > LONG_FREE (장기무이자)
                     * applicableCardProductCode 미존재 > NONE (장기무이자 X)
                     */
                    longFreeInstallmentType:
                        isCardPayMethod(cardPayMethod) && !!installment.applicableCardProductCode
                            ? LongFreeInstallmentStatus.LONG_FREE
                            : LongFreeInstallmentStatus.NONE,
                }
            })
    )
}

function isInterestBurdenPriority({
    interestBurdenType,
    installment,
}: {
    interestBurdenType: InterestBurdenTypeType
    installment: {
        /** 무이자 타입 (PG:01, MP:02, MP+SELLER:03) PG, MP, MP_SELLER */
        interestBurdenType: InterestBurdenTypeType // '01'
    }
}) {
    const InterestBurdenMap = {
        [InterestBurdenType.PG]: 0,
        [InterestBurdenType.MP]: 1,
        [InterestBurdenType.MP_SELLER]: 2,
        [InterestBurdenType.CUSTOMER]: 3,
        [InterestBurdenType.NONE]: 99,
    }
    return (
        interestBurdenType &&
        installment.interestBurdenType &&
        InterestBurdenMap[interestBurdenType] < InterestBurdenMap[installment.interestBurdenType]
    )
}

function isInterestFeePriority({
    interestFreeType,
    installment,
}: {
    interestFreeType: InterestFreeInstallmentTypeType
    installment: {
        /** 무이자 타입 (전체:ALL, 부분 PART) */
        interestFreeType: InterestFreeInstallmentTypeType // 'ALL'
    }
}) {
    const InterestFreeInstallmentMap = {
        [InterestFreeInstallmentType.ALL]: 0,
        [InterestFreeInstallmentType.PART]: 1,
        [InterestFreeInstallmentType.PRODUCT]: 2,
        [InterestFreeInstallmentType.NONE]: 99,
    }
    return (
        interestFreeType &&
        installment.interestFreeType &&
        InterestFreeInstallmentMap[interestFreeType] < InterestFreeInstallmentMap[installment.interestFreeType]
    )
}

/** 카드사 코드별 할부정보 생성 (회사별 기본 할부 n개월, n개월별로 최소 결제금액 정보 배열을 입력받음) */
export function makeInterestFreeInstallmentsByCompanyCode({
    cardCompanies,
    interestFreeInstallments,
    limitedInstallmentMonth,
}: {
    cardCompanies: {
        code: PaymentsCardCodeType
        name: string
        baseInstallmentInfo: {month: number; minAmount: number}[]
    }[]
    interestFreeInstallments: InterestFreeInstallment[]
    interestFreeStatus: InterestFreeStatusType
    /** 제한된 할부 개월수 기본 12개월 */
    limitedInstallmentMonth: Maybe<number>
}) {
    const cardCompanyCodeGroup = pipe(
        sortBy<InterestFreeInstallment>((v) => v.installmentMonth),
        groupBy<InterestFreeInstallment>((v) => v.cardCompanyCode),
    )(interestFreeInstallments)

    return cardCompanies.reduce<{
        [cardCompanyCode: string]: InterestFreeInstallment[]
    }>((acc, {code, name, baseInstallmentInfo}) => {
        const installments: InterestFreeInstallment[] = baseInstallmentInfo.map(({month, minAmount}) => {
            return {
                interestBurdenType: '',
                interestBurdenTypeName: '',
                cardCompanyCode: code,
                cardCompanyName: name,
                interestFreeType: InterestFreeInstallmentType.NONE,
                interestFreeTypeName: '',
                merchantBurdenRatio: 0,
                minAmount,
                interestFreeMinimumAmount: 0,
                installmentMonth: month,
                customerBurdenMonths: [],
                applicableCardProductCode: null,
                interestFreeCodeType: null,
            }
        })
        return {
            ...acc,
            [code]: [...installments, ...(cardCompanyCodeGroup[code] ?? [])].filter(
                ({installmentMonth}) => !limitedInstallmentMonth || installmentMonth <= limitedInstallmentMonth,
            ),
        }
    }, {})
}

/** 회사별 기본 최소 할부 가능 결제금액 반환 */
export const getCommonInstallmentMinAmount = (companyCode: PaymentsCardCodeType) => {
    switch (companyCode) {
        case PaymentsCardCode.HYUNDAI:
            return 10000
        default:
            return 50000
    }
}

/**
 * 할부정보 반환 (부분할부 정책 적용에 따라 기본할부 vs 제한된 할부 반환)
 * Case 1. 기본할부
 * 기본할부 개월수: 12개월
 * 할부가능금액: 현대카드 1만원 이상, 그 외  5만원 이상
 *
 * Case 2. 제한된 할부
 * limitedCardList 에 포함된 카드사는 일시불만 가능
 * interestInstallments 정보를 통해 baseInstallmentInfo 생성
 *
 * @params limitedCardList
 * 할부제한 카드사
 * 할부제한이 아닌 카드는 빌링정보대로,
 * 그 외에는 정책대로 일시불 적용
 * @see 1. 자동차 상품 일반판매 정책 (https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=171903798)
 * @see 2. https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2016#issuecomment-1396466
 *
 * @params interestInstallments
 * 유이자 할부정보
 **/
export const getCommonInstallments = (
    companyCode: PaymentsCardCodeType,
    limitedInfo?: {limitedCardList: string[]; interestInstallments: InterestInstallment[]},
) => {
    const nonInstallment = {month: 1, minAmount: 0}
    const installments = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => ({month}))

    if (!!limitedInfo && !isEmpty(limitedInfo?.limitedCardList) && !isEmpty(limitedInfo?.interestInstallments)) {
        if (includes(companyCode, limitedInfo?.limitedCardList)) {
            return [nonInstallment]
        } else {
            return [
                nonInstallment,
                ...limitedInfo.interestInstallments
                    .filter((installment) => eq(companyCode, installment.cardCompanyCode))
                    .map((installment) => ({
                        month: installment.installmentMonth,
                        minAmount: installment.interestMinimumAmount,
                    })),
            ]
        }
    }

    return [
        nonInstallment,
        ...installments.map((installment) => ({...installment, minAmount: getCommonInstallmentMinAmount(companyCode)})),
    ]
}

/** 할부 우선순위 로직, 중복 로직이 많아서, 추후 개선필요 */
export function getInterestFreeInstallments({
    installments,
    productCode,
}: {
    installments: InterestFreeInstallment[]
    productCode?: Maybe<string>
}): InterestFreeInstallment[] {
    const data: Record<number, InterestFreeInstallment> = {}

    installments.reduce((acc, value) => {
        const {installmentMonth, applicableCardProductCode, interestFreeType, interestBurdenType} = value
        // 롯데 카드는 12개월 무이자 정보가 applicableCardProductCode 와 함께 포함될 수 있음
        // https://oss.fin.navercorp.com/payOrder/order-qa/issues/5253#issuecomment-881527
        const installment = acc[installmentMonth] ?? null

        if (!applicableCardProductCode || productCode === applicableCardProductCode) {
            if (!installment) {
                acc[installmentMonth] = value
            } else if (installment.interestFreeType === interestFreeType) {
                if (
                    isInterestBurdenPriority({
                        installment,
                        interestBurdenType,
                    })
                ) {
                    // ALL 과 ALL 일경우 MP_SELLER 보다는 PG 가 우선
                    acc[installmentMonth] = value
                }
            } else if (
                isInterestFeePriority({
                    interestFreeType,
                    installment,
                })
            ) {
                // PART 보다는 ALL 이 우선
                acc[installmentMonth] = value
            }
        }
        return acc
    }, data)
    return Object.values(data).filter((v) => v)
}

/** 할부 우선순위 로직, 중복 로직이 많아서, 추후 개선필요 */
export function getInterestFreeInstallmentsByCard({
    installments,
}: {
    installments: InterestFreeInstallment[]
}): InterestFreeInstallment[] {
    const data: Record<number, InterestFreeInstallment> = {}

    installments.reduce((acc, value) => {
        const {installmentMonth, applicableCardProductCode, interestFreeType, interestBurdenType} = value
        // 롯데 카드는 12개월 무이자 정보가 applicableCardProductCode 와 함께 포함될 수 있음
        // https://oss.fin.navercorp.com/payOrder/order-qa/issues/5253#issuecomment-881527
        const installment = acc[installmentMonth] ?? null

        if (!installment) {
            acc[installmentMonth] = value
        } else if (!applicableCardProductCode || installment.applicableCardProductCode) {
            if (installment.interestFreeType === interestFreeType) {
                if (
                    isInterestBurdenPriority({
                        installment,
                        interestBurdenType,
                    })
                ) {
                    // ALL 과 ALL 일경우 MP_SELLER 보다는 PG 가 우선
                    acc[installmentMonth] = value
                } else if (!applicableCardProductCode && installment.applicableCardProductCode) {
                    // 일반 신용카드는 applicableCardProductCode 가 없는것이 우선
                    acc[installmentMonth] = value
                }
            } else if (
                isInterestFeePriority({
                    interestFreeType,
                    installment,
                })
            ) {
                // PART 보다는 ALL 이 우선
                acc[installmentMonth] = value
            }
        }
        return acc
    }, data)
    return Object.values(data).filter((v) => v)
}

/** 카드사 할부정보 생성 추가 (기본 12개월) > 삼성페이 사용 */
export function makeInterestFreeInstallments({
    cardCompanyCodeGroup,
}: {
    cardCompanyCodeGroup: InterestFreeInstallment[]
}): InterestFreeInstallment[] {
    const installments: InterestFreeInstallment[] = []

    function findInstallmentIndex(value: InterestFreeInstallment) {
        return installments.findIndex(
            (installment) =>
                installment && // 인증/결제형에서는 할부개월수가 연속적이지 않으므로 setInstallment시 sparse array가 생성 될 수 있음
                !installment.applicableCardProductCode &&
                installment.installmentMonth === value.installmentMonth,
        )
    }
    function getInstallment(installmentMonth: number, value: InterestFreeInstallment) {
        if (installmentMonth < 12) {
            return installments[installmentMonth - 1]
        }
        const index = findInstallmentIndex(value)
        return index > -1 ? (installments[index] ?? null) : null
    }
    function setInstallment(installmentMonth: number, value: InterestFreeInstallment) {
        if (installmentMonth < 12) {
            installments[installmentMonth - 1] = value
        } else {
            const index = findInstallmentIndex(value)
            if (index > -1) {
                installments[index] = value
            } else {
                installments.push(value)
            }
        }
    }
    cardCompanyCodeGroup.forEach((value) => {
        const {installmentMonth, applicableCardProductCode, interestFreeType, interestBurdenType} = value
        // 롯데 카드는 12개월 무이자 정보가 applicableCardProductCode 와 함께 포함될 수 있음
        if (!applicableCardProductCode) {
            // https://oss.fin.navercorp.com/payOrder/order-qa/issues/5253#issuecomment-881527
            const installment = getInstallment(installmentMonth, value)
            if (!installment) {
                setInstallment(installmentMonth, value)
            } else if (installment.interestFreeType === interestFreeType) {
                if (
                    isInterestBurdenPriority({
                        installment,
                        interestBurdenType,
                    })
                ) {
                    // ALL 과 ALL 일경우 MP_SELLER 보다는 PG 가 우선
                    setInstallment(installmentMonth, value)
                }
            } else if (
                isInterestFeePriority({
                    interestFreeType,
                    installment,
                })
            ) {
                // PART 보다는 ALL 이 우선
                setInstallment(installmentMonth, value)
            }
        } else {
            installments.push(value)
        }
    })

    return installments.filter((v) => v)
}

// 무이자 적용 가능한 카드 상품 코드
const applicableCardProductCodes = [
    'C313807', //  롯데 쇼핑앤로카
    'C313808', //  롯데 쇼핑앤로카
    'C313809', //  롯데 쇼핑앤로카
    'C313810', //  롯데 쇼핑앤로카
    'C313811', //  롯데 쇼핑앤로카
    'C313812', //  롯데 쇼핑앤로카
    'C314088', //  롯데 쇼핑앤로카
    'C314089', //  롯데 쇼핑앤로카
]
// web/pages/order,  web/pages/smartstore, web/pages/fleeMarket 에서 사용중 > 추후 제거 가능 확인
export const isApplicableCardProductCodes = (productCode: string) => {
    return applicableCardProductCodes.includes(productCode)
}

/**
 * 장기 유이자 보강
 **/
export const setupLongInterestFreeInstallments = (
    interestFreeInstallments: InterestFreeInstallment[],
    interestInstallments: {
        cardCompanyCode: string
        cardCompanyName: string
        installmentMonth: number
        interestMinimumAmount: number
        applicableCardProductCode?: Maybe<string>
        applicableCardProductName?: Maybe<string>
        showCommissionRate?: Maybe<boolean>
        customerBurdenCommissionRate?: Maybe<string>
    }[],
) => {
    return [
        ...interestInstallments
            // @see https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2754#issue-648894 12개월 초과시
            .filter(({installmentMonth}) => installmentMonth > 12)
            .map(
                ({
                    cardCompanyCode,
                    cardCompanyName,
                    installmentMonth,
                    interestMinimumAmount,
                    applicableCardProductCode,
                    applicableCardProductName,
                    showCommissionRate,
                    customerBurdenCommissionRate,
                }) => {
                    const result: InterestFreeInstallment = {
                        interestBurdenType: '',
                        interestBurdenTypeName: '',
                        cardCompanyCode,
                        cardCompanyName,
                        interestFreeType: InterestFreeInstallmentType.NONE,
                        interestFreeTypeName: '',
                        merchantBurdenRatio: 0,
                        interestFreeMinimumAmount: interestMinimumAmount,
                        installmentMonth,
                        customerBurdenMonths: [],
                        applicableCardProductCode: applicableCardProductCode ?? null,
                        applicableCardProductName,
                        customerBurdenCommissionRate: customerBurdenCommissionRate ?? undefined,
                        showCustomerBurdenCommissionRate: showCommissionRate ?? undefined,
                        minAmount: getCommonInstallmentMinAmount(cardCompanyCode as PaymentsCardCodeType),
                        interestFreeCodeType: null,
                    }
                    return result
                },
            ),
        ...interestFreeInstallments,
    ]
}

/** 간편카드 할부배열 및 카드사 할부배열 반환함수 */
export const getEasyCardInstallmentInfo = ({
    card,
    interestFreeInstallments,
    paymentAmount,
}: {
    /** 카드 */
    card: CardView
    /** 모든 카드사 할부배열 */
    interestFreeInstallments: InterestFreeInstallment[]
    /** 결제금액 */
    paymentAmount: number
}) => {
    const filteredInterestFreeInstallments = getInterestFreeInstallments({
        installments: interestFreeInstallments.filter(({interestFreeMinimumAmount, interestFreeType}) => {
            return (
                interestFreeMinimumAmount <= paymentAmount &&
                (card.ownerType === CardOwnerGroup.CORPORATION || card.ownerType === null
                    ? interestFreeType === InterestFreeInstallmentType.NONE
                    : true)
            )
        }),
        productCode: card.productCode,
    })

    const easyCardInstallments = buildCardInstallmentOptions({
        paymentAmount,
        cardPayMethod: 'CARD_EASY_PAY',
        ownerType: card.ownerType,
        companyCode: card.companyCode,
        cardProductCode: card.productCode,
        cardType: card.type,
        installments: filteredInterestFreeInstallments,
    })

    return {
        /** 특정 카드사 할부배열 */
        installments: filteredInterestFreeInstallments,
        /** 특정 카드 할부배열 */
        easyCardInstallments,
    }
}

/** 일반카드 할부배열 및 카드사 할부배열 반환함수 */
export const getCardInstallmentInfo = ({
    card,
    interestFreeInstallments,
    paymentAmount,
}: {
    /** 카드 */
    card: {
        /** 카드 구분 개인카드 | 법인카드 */
        type: CardCodeType
        /** 선택된 카드사 코드 */
        companyCode: Maybe<PaymentsCardCodeType>
        /** 할부 기간 */
        installment: string
        /** 앱카드 정보 */
        appCardInfo: Maybe<{
            /** 이름 */
            name: string
            /** 직접호출 사용 여부  */
            useDirectCall: boolean
        }>
    }
    /** 모든 카드사 할부배열 */
    interestFreeInstallments: InterestFreeInstallment[]
    /** 결제금액 */
    paymentAmount: number
}) => {
    const filteredInterestFreeInstallments = getInterestFreeInstallmentsByCard({
        installments: interestFreeInstallments.filter(({interestFreeMinimumAmount, interestFreeType}) => {
            return (
                interestFreeMinimumAmount <= paymentAmount &&
                (card.type === CardOwnerGroup.CORPORATION
                    ? interestFreeType === InterestFreeInstallmentType.NONE
                    : true)
            )
        }),
    })

    const cardInstallments = buildCardInstallmentOptions({
        paymentAmount,
        cardPayMethod: 'CARD',
        companyCode: card.companyCode ?? '',
        ownerType: card.type,
        /**
         * 유니온 페이, 카카오뱅크는 일반결제시 할부 불가 (https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=153889515#id-123.주문서>결제수단-화면구성-일반결제(신용카드))
         * 유니온페이 및 해외발급 카드는 간편카드에는 추가될 수 없음
         * 카카오 페이는 체크카드만 발급되므로 할부 미적용
         */
        nonInstallmentCardCorps: [PaymentsCardCode.UNIONPAY, PaymentsCardCode.KAKAO],
        installments: filteredInterestFreeInstallments,
    })

    return {
        /** 특정 카드사 할부배열 */
        installments: filteredInterestFreeInstallments,
        /** 특정 카드 할부배열 */
        cardInstallments,
    }
}

export const isVaildAsSpecialRateInstallment = (installment: InterestFreeInstallment) => {
    return (
        !!installment.showCustomerBurdenCommissionRate &&
        !!installment.customerBurdenCommissionRate &&
        +installment.customerBurdenCommissionRate > 0
    )
}
